import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Shield } from "lucide-react";
import { ThemeToggle } from "../components/ThemeToggle";
import { useTheme } from "../context/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Theme-aware logos (SVG with transparent background)
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_glass-teach/artifacts/drr8t3sh_Dark_2.svg";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_glass-teach/artifacts/6ix3baet_Light_2.svg";

export const AdminLogin = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const logoUrl = theme === 'dark' ? LOGO_DARK : LOGO_LIGHT;
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/admin`, { password });
      localStorage.setItem("mms_token", response.data.access_token);
      localStorage.setItem("mms_user_type", response.data.user_type);
      toast.success("Admin login successful!");
      navigate("/admin");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid admin password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen relative overflow-hidden flex items-center justify-center p-4 ${theme === 'dark' ? 'bg-zinc-950' : 'bg-slate-50'}`}>
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {theme === 'dark' ? (
          <>
            <div className="absolute top-[-30%] left-[-20%] w-[60%] h-[60%] bg-gradient-to-r from-zinc-800 to-zinc-900 rounded-full blur-3xl opacity-20" />
            <div className="absolute bottom-[-30%] right-[-20%] w-[60%] h-[60%] bg-gradient-to-l from-zinc-800 to-zinc-900 rounded-full blur-3xl opacity-20" />
          </>
        ) : (
          <>
            <div className="absolute top-[-30%] left-[-20%] w-[60%] h-[60%] bg-gradient-to-r from-indigo-100 to-blue-100 rounded-full blur-3xl opacity-50" />
            <div className="absolute bottom-[-30%] right-[-20%] w-[60%] h-[60%] bg-gradient-to-l from-purple-100 to-indigo-100 rounded-full blur-3xl opacity-50" />
          </>
        )}
      </div>

      {/* Back Button & Theme Toggle */}
      <div className="absolute top-6 left-6 right-6 flex justify-between items-center z-10">
        <button
          onClick={() => navigate("/")}
          className={`flex items-center gap-2 transition-colors ${theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`}
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>
        <ThemeToggle />
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative glass-card p-8 md:p-12 w-full max-w-md"
      >
        <div className="flex flex-col items-center mb-8">
          <img src={logoUrl} alt="MMS Logo" className="h-20 w-auto object-contain mb-4 logo-glass" />
          <div className="flex items-center gap-2 mb-2">
            <Shield className={`w-6 h-6 ${theme === 'dark' ? 'text-white' : 'text-indigo-600'}`} />
            <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>Admin Access</h1>
          </div>
          <p className={`text-center ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
            Enter the admin password to access the management dashboard
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label className="form-label">Admin Password</label>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="glass-input w-full"
              placeholder="••••••••••••"
              required
            />
          </div>
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="glass-button-primary w-full disabled:opacity-50"
          >
            {loading ? "Authenticating..." : "Access Dashboard"}
          </button>
        </form>
      </motion.div>
    </div>
  );
};
